import Events from '@utilities/events';
import keyboardEventCode from '@utilities/keyboard-event-code';
import IsBasicKey from '@utilities/is-basic-key';
import isIos from '@utilities/detect-os';
import debounce from 'lodash/debounce';
import API from '@utilities/api';
import ScreenDimensions from '@utilities/screen-dimensions';
import { body, html } from '@utilities/dom-elements';

const JS_HOOK_SEARCH_TOGGLE = '[js-hook-search-toggle]';
const JS_HOOK_SUGGESTIONS_HOLDER = '[js-hook-search-suggestions-holder]';
const JS_HOOK_HEADER = '[js-hook-header]';
const JS_HOOK_SEARCH_FORM_WRAPPER = '[js-hook-search-form-wrapper]';
const JS_HOOK_SEARCH_FORM = '[js-hook-search-form]';
const JS_HOOK_SEARCH_SUGGESTIONS = '[js-hook-search-suggestions]';
const JS_HOOK_SEARCH_INPUT = '[js-hook-search-input]';
const JS_HOOK_SUBMIT = '[js-hook-submit]';
const JS_HOOK_CLEAR = '[js-hook-clear]';
const JS_HOOK_CLOSE_BUTTON = '[js-hook-search-suggestions-close]';
const CLASS_SEARCH_HTML = 'search--is-open';
const CLASS_SEARCH_INPUT_HTML = 'search--is-in-focus';
const CLASS_ACTIVE = 'is--active';
const HIDDEN_CLASS = 'u-hidden';

class Search {
  constructor(element) {
    this.element = element;

    this.ui = {
      header: document.querySelector(JS_HOOK_HEADER),
      formWrapper: this.element.querySelector(JS_HOOK_SEARCH_FORM_WRAPPER),
      form: this.element.querySelector(JS_HOOK_SEARCH_FORM),
      toggleButton: this.element.querySelector(JS_HOOK_SEARCH_TOGGLE),
      clearButton: this.element.querySelector(JS_HOOK_CLEAR),
      suggestions: this.element.querySelector(JS_HOOK_SEARCH_SUGGESTIONS),
      suggestionsHolder: this.element.querySelector(JS_HOOK_SUGGESTIONS_HOLDER),
      searchInput: this.element.querySelector(JS_HOOK_SEARCH_INPUT),
      closeButton: this.element.querySelector(JS_HOOK_CLOSE_BUTTON),
      submitButton: this.element.querySelector(JS_HOOK_SUBMIT)
    };

    this.settings = {
      endpoint: this.ui.suggestionsHolder.dataset.url,
      minChars: 3,
      isOpen: false,
      lastScrollTop: false,
    };

    this.bindEvents();
  }

  showSearchForm() {
    Events.$trigger('search::input-open');
    this.ui.formWrapper.classList.add(CLASS_ACTIVE);
    html.classList.add(CLASS_SEARCH_INPUT_HTML);

    // without timeout causes page to scroll/jump up
    setTimeout(() => {
      this.ui.searchInput.focus();
    }, 400);
  }

  hideSearchForm() {
    this.ui.formWrapper.classList.remove(CLASS_ACTIVE);
    html.classList.remove(CLASS_SEARCH_INPUT_HTML);
  }

  modalOpen() {
    this.settings.isOpen = true;
    html.classList.add(CLASS_SEARCH_HTML);

    if (isIos) {
      // Scroll body to top to fix the iOs keyboard overlay which pushes the website up
      body.scrollTop = this.settings.lastScrollTop;
      body.scrollTop = 0;
    }

    Events.$trigger('focustrap::activate', {
      data: {
        element: this.element,
        autoFocus: false,
      },
    });

    this.ui.searchInput.focus();
  }

  modalClose() {
    this.settings.isOpen = false;

    if (this.settings.lastScrollTop) {
      body.scrollTop = this.settings.lastScrollTop;
      this.settings.lastScrollTop = null;
    }

    html.classList.remove(CLASS_SEARCH_HTML);

    Events.$trigger('focustrap::deactivate');

    this.ui.searchInput.value = '';
    this.ui.searchInput.blur();

    if (!ScreenDimensions.isTabletLandscapeAndBigger) {
      this.clearSuggestions();
    }
  }

  /**
   * Bind all general events
   */
  bindEvents() {
    const debounceGetSuggestions = debounce((e) => this.getSuggestions(e), 400);

    Events.$on(`modal[modal-search]::open`, () => this.modalOpen());
    Events.$on(`modal[modal-search]::close`, () => this.modalClose());

    this.ui.toggleButton.addEventListener('click', () => this.showSearchForm());

    this.ui.closeButton.addEventListener('click', () => {
      Events.$trigger('modal[modal-search]::close');
      this.hideSearchForm();
    });

    this.ui.clearButton.addEventListener('click', () => this.clearSuggestions(true, true));

    this.ui.searchInput.addEventListener('keydown', (event) => this.onTyping(event));

    this.ui.searchInput.addEventListener('keyup', (e) => debounceGetSuggestions(e));

    // this.ui.clearButton.addEventListener('click', (e) => {
    //   e.preventDefault();
    //   this.ui.form.reset();
    // });

    this.ui.form.addEventListener('submit', (e) => {
      if (this.ui.searchInput.value === '') {
        e.preventDefault();
      } else {
        Events.$trigger('gtm::push', {
          data: {
            event: 'search',
            term: this.ui.searchInput.value
          }
        });
      }
    });

    if (!ScreenDimensions.isTabletLandscapeAndBigger && !this.settings.isOpen) {
      this.ui.submitButton.addEventListener('click', () =>
        Events.$trigger('modal[modal-search]::open'),
      );
    }
  }

  toggleSuggestionResults(action) {
    this.ui.suggestionsHolder.classList[action === 'show' ? 'remove' : 'add'](HIDDEN_CLASS);
  }

  onTyping(event) {
    const key = event.key || event.keyCode;

    // If the search is not open yet, let enter open the search in stead of submitting
    if (!this.settings.isOpen && keyboardEventCode.enter.indexOf(key) !== -1) {
      event.preventDefault();
    }

    if (!this.settings.isOpen && this.ui.searchInput.value.length >= this.settings.minChars) {
      Events.$trigger('loader::show', {
        data: {
          lightTheme: true,
          target: this.ui.form,
        },
      });
    }

    if (keyboardEventCode.escape.indexOf(event.keyCode || event.key) !== -1) {
      Events.$trigger('loader::hide');
    }
  }

  /**
   * Process Ajax response for SearchServices-GetSuggestions
   *
   * @param {Object|string} response - Empty object literal if null response or string with rendered
   *                                   suggestions template contents
   */
  getSuggestionsSuccess(response) {
    // Stop Loading
    Events.$trigger('loader::hide');

    // Empty Suggestions
    this.ui.suggestionsHolder.innerHTML = '';

    const parser = new DOMParser();
    const content = parser.parseFromString(response, 'text/html').body;

    if (!content) {
      this.clearSuggestions();
      return;
    }

    this.ui.suggestionsHolder.innerHTML = content.firstChild.innerHTML;

    // Lazyloaded images
    Events.$trigger('lazyimage::update');

    // Bind All Events
    Events.$trigger('events::dom-reinit');
    Events.$trigger('modal[modal-search]::open');

    // Chat buttons rebind event
    Events.$trigger('chat::reinit-buttons');
  }

  /**
   * Retrieve suggestions
   */
  getSuggestions(e) {
    const key = e.keyCode || e.key;
    const allowKey = IsBasicKey(key);

    if (!allowKey && (!this.settings.isOpen ? keyboardEventCode.enter.indexOf(key) === -1 : true)) {
      return;
    }

    const value = this.ui.searchInput.value || '';

    if (value.length >= this.settings.minChars) {
      Events.$trigger('loader::show', {
        data: {
          lightTheme: true,
          target: ScreenDimensions.isTabletLandscapeAndBigger ? this.ui.suggestions : this.ui.form,
        },
      });

      API.get(this.settings.endpoint + encodeURIComponent(value))
        .then((response) => this.getSuggestionsSuccess(response.data))
        .catch(() => this.clearSuggestions(true));
    }
  }

  /**
   * Clear suggestions div
   */
  clearSuggestions(clearInput, focusInput) {
    Events.$trigger('loader::hide');
    this.ui.suggestionsHolder.innerHTML = '';

    if (clearInput) {
      this.ui.searchInput.value = '';
    }

    if (focusInput) {
      this.ui.searchInput.focus();
    }
  }
}

export default Search;
